import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CirclePlay, CircleStop, Eye, Menu, MoreVertical, Trash2 } from 'lucide-react';
import { Button } from 'components/ui/button';
import { StatusBadge } from 'components/ui/status-badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { PhoneStepBar } from 'components/workspace/PhoneStepBar';
import { useWorkspaceLiveState } from 'components/workspace/WorkspaceLive';
import { currentStepIndex, lifecycleSteps } from 'components/workspace/lifecycle';
import { useTestConfigSidebar } from './TestConfigSidebarContext';
import { useTestLifecycle } from './TestLifecycleContext';
import { TestStatusBadge } from './TestStatusBadge';

const ICON_BUTTON =
  'flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-foreground/80 transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring';

/**
 * The phone workspace header (<768px, 56px): sections menu, the title over
 * "Testlar", the status (or "Jonli" while a live page is open) and a menu with
 * Preview, the lifecycle action and Delete.
 */
export const TestPhoneHeader: React.FC = () => {
  const { t } = useTranslation();
  const { toggle: toggleRail } = useTestConfigSidebar();
  const lc = useTestLifecycle();
  const live = useWorkspaceLiveState();
  const { phase } = lc;

  return (
    <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border bg-card px-1.5">
      <button type="button" onClick={toggleRail} aria-label={t('workspace_rail_label')} className={ICON_BUTTON}>
        <Menu className="h-5 w-5" />
      </button>
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-[15px] font-semibold leading-5 text-foreground">{lc.title}</span>
        <span className="text-xs leading-4 text-muted-foreground">{t('nav.tests')}</span>
      </div>
      {live ? (
        <StatusBadge tone={live.connected ? 'success' : 'neutral'} className="shrink-0">
          {live.connected ? t('monitor_live') : t('connecting')}
        </StatusBadge>
      ) : (
        <TestStatusBadge status={lc.status} className="shrink-0" />
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" aria-label={t('more_options')} className={ICON_BUTTON}>
            <MoreVertical className="h-5 w-5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[210px]">
          <DropdownMenuItem className="gap-2" asChild>
            <Link to={`/app/tests/preview/${lc.testId}`}>
              <Eye className="h-4 w-4" />
              {t('preview')}
            </Link>
          </DropdownMenuItem>
          {phase === 'draft' && (
            <DropdownMenuItem
              className="gap-2"
              disabled={!lc.readiness.allReady || lc.activating}
              onSelect={lc.requestActivate}
            >
              <CirclePlay className="h-4 w-4" />
              {t('activate_test')}
            </DropdownMenuItem>
          )}
          {phase === 'active' && (
            <DropdownMenuItem className="gap-2" disabled={lc.finishing} onSelect={lc.finish}>
              <CircleStop className="h-4 w-4" />
              {t('finish_test')}
            </DropdownMenuItem>
          )}
          {phase === 'finished' && (
            <DropdownMenuItem className="gap-2" disabled={lc.activating} onSelect={lc.requestActivate}>
              <CirclePlay className="h-4 w-4" />
              {t('reactivate_test')}
            </DropdownMenuItem>
          )}
          {lc.canDelete && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="gap-2 text-destructive focus:text-destructive" onSelect={lc.requestDelete}>
                <Trash2 className="h-4 w-4" />
                {t('delete_assessment')}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
};

/** A draft's progress under the phone header: "1/3 · Yig'ish" and how many checks are left. */
export const TestPhoneSteps: React.FC = () => {
  const { t } = useTranslation();
  const lc = useTestLifecycle();
  if (lc.phase !== 'draft') return null;
  const step = currentStepIndex(lifecycleSteps(lc.phase, lc.readiness.allReady));
  return (
    <PhoneStepBar
      step={step}
      note={lc.checksRemaining > 0 ? t('workspace_checks_left', { count: lc.checksRemaining }) : undefined}
    />
  );
};

/** The phone Overview's sticky bottom bar: a draft's one lime action. */
export const TestPhoneActivateBar: React.FC = () => {
  const { t } = useTranslation();
  const lc = useTestLifecycle();
  if (lc.phase !== 'draft') return null;
  return (
    <div className="shrink-0 border-t border-border bg-card px-4 pb-[calc(12px+env(safe-area-inset-bottom))] pt-3">
      <Button
        size="lg"
        className="h-12 w-full"
        disabled={!lc.readiness.allReady || lc.activating}
        onClick={lc.requestActivate}
      >
        {t('activate_test')}
      </Button>
    </div>
  );
};

export default TestPhoneHeader;
